import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaCheckCircle, FaClock } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import { getRevealProps } from '../motion/scrollMotion';
import { buildMailtoLink, sendSubmission } from '../utils/mailer';
import './ServiceDetailPage.css';
import './PentestQuotePage.css';

const targetTypes = [
  'Web Application', 'Mobile App (iOS/Android)', 'API', 'Internal Network',
  'External Network', 'Cloud Environment', 'Not sure yet',
];

const sizeOptions = [
  '1 application or under 10 IPs',
  '2–5 applications or 10–50 IPs',
  '6+ applications or 50+ IPs',
  'Not sure yet',
];

const complianceOptions = [
  'ISO 27001', 'PCI DSS', 'SOC 2', 'A client or partner requirement', 'No specific driver', 'Not sure',
];

const timelineOptions = [
  'ASAP — within 2 weeks', 'Within a month', 'Within a quarter', 'Just exploring options',
];

const PentestQuotePage = () => {
  const [form, setForm] = useState({
    targets: [], size: '', compliance: '', timeline: '', fullName: '', email: '', company: '', notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState('sent'); // 'sent' = delivered to us; 'mailto' = handed to their mail client
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const toggleTarget = (t) => {
    setForm((prev) => ({
      ...prev,
      targets: prev.targets.includes(t) ? prev.targets.filter((x) => x !== t) : [...prev.targets, t],
    }));
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError('');

    const fields = {
      Name: form.fullName,
      Email: form.email,
      Company: form.company,
      'What needs testing': form.targets.join(', ') || 'Not specified',
      'Target size': form.size,
      'Compliance driver': form.compliance,
      Timeline: form.timeline,
      Notes: form.notes || '—',
    };

    setSending(true);
    try {
      await sendSubmission({ source: 'Pentest Quote', fields, replyTo: form.email });
      setMode('sent');
      setSubmitted(true);
    } catch (err) {
      // Backend unavailable — hand off to the visitor's own mail client.
      window.location.href = buildMailtoLink(
        `Pentest Quote — ${form.company || form.fullName || 'Website Inquiry'}`,
        [
          `Name: ${form.fullName}`,
          `Email: ${form.email}`,
          `Company: ${form.company}`,
          `What needs testing: ${fields['What needs testing']}`,
          `Target size: ${form.size}`,
          `Compliance driver: ${form.compliance}`,
          `Timeline: ${form.timeline}`,
          form.notes ? `\nAdditional notes:\n${form.notes}` : '',
        ]
      );
      setMode('mailto');
      setSubmitted(true);
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="svc-page">
        <PageNavbar />
        <section className="svc-hero">
          <div className="svc-hero-glow" />
          <div className="svc-container">
            <motion.div className="pq-success" {...getRevealProps('fadeUp')}>
              <FaCheckCircle className="pq-success-icon" />
              <h1 className="svc-hero-title">
                {mode === 'sent'
                  ? 'Thanks — your request is with our team.'
                  : 'Your email app should now open with your scope ready to go.'}
              </h1>
              <p className="svc-hero-desc">
                {mode === 'sent'
                  ? 'We have received your scope and a senior tester will review it and come back with a formal, scoped quote within one business day. We do not send generic price lists — pentest pricing genuinely depends on scope, and we would rather get that right than fast.'
                  : 'Hit send there to reach us. A senior tester will review what you shared and come back with a formal, scoped quote within one business day. We do not send generic price lists — pentest pricing genuinely depends on scope, and we would rather get that right than fast.'}
              </p>
              <Link to="/services/vapt" className="svc-btn svc-btn-primary">
                Read About Our VAPT Methodology <FaArrowRight />
              </Link>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="svc-page">
      <PageNavbar />

      <section className="svc-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container">
          <motion.div className="pq-hero-text" {...getRevealProps('fadeUp')}>
            <span className="svc-eyebrow">Free, No-Obligation Quote</span>
            <h1 className="svc-hero-title">Get a Penetration Testing Quote</h1>
            <p className="svc-hero-desc">
              We will not fake you out with an instant number — real pentest pricing depends on
              scope, and generic per-page pricing calculators are how you end up either
              overpaying or under-scoped. Tell us what you need tested, and a senior tester will
              send a real, scoped quote within one business day.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="svc-section pq-form-section">
        <div className="svc-container pq-form-container">
          <form className="pq-form" onSubmit={handleSubmit}>
            <div className="pq-field">
              <label>What do you need tested? (select all that apply)</label>
              <div className="pq-chip-group">
                {targetTypes.map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`pq-chip ${form.targets.includes(t) ? 'is-selected' : ''}`}
                    onClick={() => toggleTarget(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="pq-field">
              <label htmlFor="size">Roughly how large is the target environment?</label>
              <select id="size" name="size" value={form.size} onChange={handleChange} required>
                <option value="">Select a size...</option>
                {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="pq-field-row">
              <div className="pq-field">
                <label htmlFor="compliance">What's driving this engagement?</label>
                <select id="compliance" name="compliance" value={form.compliance} onChange={handleChange} required>
                  <option value="">Select a driver...</option>
                  {complianceOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="pq-field">
                <label htmlFor="timeline">Desired timeline</label>
                <select id="timeline" name="timeline" value={form.timeline} onChange={handleChange} required>
                  <option value="">Select a timeline...</option>
                  {timelineOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="pq-divider" />

            <div className="pq-field-row">
              <div className="pq-field">
                <label htmlFor="fullName">Full Name *</label>
                <input type="text" id="fullName" name="fullName" value={form.fullName} onChange={handleChange} required placeholder="Your full name" />
              </div>
              <div className="pq-field">
                <label htmlFor="email">Business Email *</label>
                <input type="email" id="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@company.com" />
              </div>
            </div>

            <div className="pq-field">
              <label htmlFor="company">Company *</label>
              <input type="text" id="company" name="company" value={form.company} onChange={handleChange} required placeholder="Your company name" />
            </div>

            <div className="pq-field">
              <label htmlFor="notes">Anything else we should know?</label>
              <textarea id="notes" name="notes" value={form.notes} onChange={handleChange} rows="4" placeholder="Optional — specific concerns, prior findings, systems to exclude, etc." />
            </div>

            <button type="submit" className="svc-btn svc-btn-primary pq-submit" disabled={sending}>
              {sending ? 'Sending…' : <>Request My Quote <FaArrowRight /></>}
            </button>
            {error && <p className="pq-error-note" role="alert">{error}</p>}
            <p className="pq-response-note"><FaClock /> We respond to every request within one business day.</p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PentestQuotePage;
